import { useEffect, useState } from "react";
import { Play } from "lucide-react";

/**
 * Designed photo/video slot. Renders the real asset once the file is dropped
 * into /images/<file>; until then shows an intentional framed placeholder.
 */
export const PhotoSlot = ({ file, label, kind = "photo", className = "", aspect = "aspect-[4/5]", parallaxStyle, imgPosition = "center" }) => {
  const [src, setSrc] = useState(null);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    let active = true;
    if (kind === "video") {
      if (!file) return;
      setVideoError(false);
    } else if (file) {
      const img = new Image();
      img.onload = () => active && setSrc(`/images/${file}`);
      img.onerror = () => {};
      img.src = `/images/${file}`;
    }
    return () => {
      active = false;
    };
  }, [file, kind]);

  const showVideo = kind === "video" && file && !videoError;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-white/10 bg-surface ${aspect} ${className}`}
      style={parallaxStyle}
      data-testid={file ? `photo-slot-${file.replace(/\.[^.]+$/, "")}` : "photo-slot"}
    >
      {src && kind === "photo" && (
        <img
          src={src}
          alt={label || "Agency HQ team"}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: imgPosition }}
          loading="lazy"
        />
      )}
      {showVideo && (
        <video
          src={`/images/${file}`}
          controls
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-contain bg-ink"
          onError={() => setVideoError(true)}
        />
      )}
      {!src && !showVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
          {/* corner ticks */}
          <span className="absolute left-4 top-4 h-4 w-4 border-l border-t border-gold/60" aria-hidden="true" />
          <span className="absolute right-4 top-4 h-4 w-4 border-r border-t border-gold/60" aria-hidden="true" />
          <span className="absolute bottom-4 left-4 h-4 w-4 border-b border-l border-gold/60" aria-hidden="true" />
          <span className="absolute bottom-4 right-4 h-4 w-4 border-b border-r border-gold/60" aria-hidden="true" />
          <span className="font-display text-6xl font-extrabold text-white/[0.06] select-none" aria-hidden="true">
            HQ
          </span>
          {kind === "video" && (
            <span className="absolute flex h-14 w-14 items-center justify-center rounded-full border border-gold/50 bg-ink/70">
              <Play className="h-5 w-5 text-gold-light" fill="currentColor" />
            </span>
          )}
          <p className="label-mono mt-6">{kind === "video" ? "Video testimonial" : "Photography"}</p>
          <p className="mt-2 text-xs text-slate-500 font-mono">
            {file ? `images/${file}` : "awaiting file"}
          </p>
          {label && <p className="mt-3 max-w-[220px] text-sm text-slate-400">{label}</p>}
        </div>
      )}
      {src && kind === "photo" && (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" aria-hidden="true" />
      )}
    </div>
  );
};

export default PhotoSlot;
